import _ from 'lodash';
import { IParamConfigurationInDTO } from '../repository/jago/model/input/IParamConfigurationInDTO';
import { LocalStorageRepository } from '../services/StorageService/LocalStorageRepository';
import { DEMO_WHITELIST_DOMAIN_PREFIX, DEMO_WHITELIST_DOMAIN_SUFFIX } from './AppConstants';

const vashoTokenStorage = new LocalStorageRepository();

function stripBearerPrefix(token: string): string {
  return token.replace(/^Bearer\s+/i, '');
}

export function readVashoAuthToken(): string {
  const storedToken = vashoTokenStorage.readKey<string>('token', '');
  if (typeof storedToken === 'string' && storedToken.trim()) {
    return stripBearerPrefix(storedToken.trim());
  }
  try {
    const raw = localStorage.getItem('token');
    if (!raw) {
      return '';
    }
    const parsed = raw.startsWith('"') ? JSON.parse(raw) : raw;
    return stripBearerPrefix(String(parsed).trim());
  } catch (error) {
    return '';
  }
}

export function getParamConfigValue(paramConfigs: IParamConfigurationInDTO[], key: string, fallback = ''): string {
  const config = _.find(paramConfigs, { keyConfig: key });
  return config?.valueConfig || fallback;
}

export function parseCommaSeparatedValue(value: string): string[] {
  if (!value || !value.trim()) {
    return [];
  }
  return _.uniq(value.split(',').map((item) => _.trim(item)).filter(Boolean));
}

export function serializeCommaSeparatedValue(items: string[]): string {
  return items.join(',');
}

export function encodeDemoWhitelistDomain(domainName: string): string {
  const trimmed = _.trim(domainName);
  if (!trimmed) {
    return '';
  }
  if (/^api-.+\.jago\.cloud$/.test(trimmed)) {
    return trimmed;
  }
  return `${DEMO_WHITELIST_DOMAIN_PREFIX}${trimmed}${DEMO_WHITELIST_DOMAIN_SUFFIX}`;
}

export function decodeDemoWhitelistDomain(hostname: string): string {
  const trimmed = _.trim(hostname);
  if (!trimmed) {
    return '';
  }
  return trimmed
    .replace(/^api-/, '')
    .replace(/\.jago\.cloud$/, '');
}

export function parseDemoWhitelistValue(value: string): string[] {
  return parseCommaSeparatedValue(value).map(decodeDemoWhitelistDomain);
}

export function serializeDemoWhitelistValue(domainNames: string[]): string {
  return serializeCommaSeparatedValue(domainNames.map(encodeDemoWhitelistDomain));
}

export function isValidDemoWhitelistDomainName(domainName: string): boolean {
  const trimmed = _.trim(domainName);
  return /^[a-zA-Z0-9-]+(\.staging)?$/.test(trimmed);
}

export function isSalesAppLaunchpadBaseUrl(value: string): boolean {
  try {
    const url = new URL(_.trim(value));
    const path = url.pathname.replace(/\/embed\/launchpad/gi, '/launchpad');
    return /\/launchpad\/?$/i.test(path) || path.includes('/launchpad/');
  } catch (error) {
    return false;
  }
}

export function resolveSalesAppsLaunchpadPath(base: string): string {
  const trimmed = _.trimEnd(_.trim(base), '/');
  return trimmed.replace(/\/embed\/launchpad$/i, '/launchpad');
}

export function isValidSalesAppBaseUrl(value: string): boolean {
  const trimmed = _.trim(value);
  if (!trimmed) {
    return false;
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return false;
    }
    const queryKeys = [...url.searchParams.keys()];
    if (queryKeys.length === 0) {
      return true;
    }
    if (isSalesAppLaunchpadBaseUrl(trimmed)) {
      return queryKeys.every((key) => key === 'token');
    }
    return false;
  } catch (error) {
    return false;
  }
}

export function normalizeSalesAppBaseUrl(value: string): string {
  const trimmed = _.trim(value);
  try {
    const url = new URL(trimmed);
    url.search = '';
    url.hash = '';
    const path = _.trimEnd(url.pathname, '/') || '/';
    return `${url.origin}${path === '/' ? '' : path}`;
  } catch (error) {
    return _.trimEnd(trimmed, '/');
  }
}

export interface ISalesAppsIframeUrlParams {
  language: string;
  clusterNames: string;
  userEmail: string;
  featureLevel: 'full' | 'standard';
  vashoToken: string;
}

export function buildSalesAppsIframeUrl(
  configValue: string,
  params: ISalesAppsIframeUrlParams,
): string {
  const base = resolveSalesAppBaseUrl(configValue);
  if (!base) {
    return '';
  }
  if (isSalesAppLaunchpadBaseUrl(base)) {
    const launchpadBase = resolveSalesAppsLaunchpadPath(base);
    const launchpadUrl = new URL(`${launchpadBase}/`);
    if (params.vashoToken) {
      launchpadUrl.searchParams.set('token', params.vashoToken);
    }
    return launchpadUrl.toString();
  }
  const lang = params.language || 'en';
  const userParam = params.userEmail ? `&user=${encodeURIComponent(params.userEmail)}` : '';
  const profileParam = params.featureLevel === 'full' ? '&featureLevel=full' : '&featureLevel=standard';
  return `${base}/?lang=${lang}&cluster=${encodeURIComponent(params.clusterNames)}${userParam}${profileParam}`;
}

export function resolveSalesAppBaseUrl(configValue: string): string {
  const trimmed = _.trim(configValue);
  if (!isValidSalesAppBaseUrl(trimmed)) {
    return '';
  }
  return normalizeSalesAppBaseUrl(trimmed);
}
